import React from 'react';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { Divider, SvgIconProps } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import Icon, { AvailableIconTypes } from '@ui/Icon';
import CircularLoader from 'components/LoadingIndicators/CircularLoader';
import NavigationTab from './NavigationTab';
import { routeIcons, secondaryRoutes } from './routes';
import { useStudioRoutesQuery } from './operation.generated';
import FallbackNavigationTabs from './FallbackNavigationTabs';

export function getBasePathFromRoute(routePath: string): string {
  return routePath.split('/').slice(0, 3).join('/');
}

export const getTabIcon =
  (iconType: AvailableIconTypes) =>
  ({ className, fontSize }: SvgIconProps) => {
    return <Icon type={iconType} className={className} fontSize={fontSize} />;
  };

const useStyles = makeStyles()(theme => ({
  list: {
    transition: theme.transitions.create(['padding'], {
      easing: 'cubic-bezier(0, 0, 0.2, 1)',
      duration: 800,
    }),
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '16px 0px',
  },
  mainNavList: {
    paddingTop: theme.spacing(1),
  },
  loader: {
    display: 'flex',
    height: 268,
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

const NavigationTabs = ({ isLeftRailOpen }: { isLeftRailOpen: boolean }) => {
  const { classes, cx } = useStyles();
  const { data, loading, error: queryError } = useStudioRoutesQuery();
  const error = !data?.studioRoutes && queryError;

  return (
    <NavigationMenu.Root orientation="vertical" aria-label="Main">
      <NavigationMenu.List className={cx(classes.list, classes.mainNavList)}>
        {loading && (
          <div className={classes.loader}>
            <CircularLoader size={16} />
          </div>
        )}
        {!loading && error && (
          <FallbackNavigationTabs isLeftRailOpen={isLeftRailOpen} />
        )}
        {!loading &&
          data?.studioRoutes.map(tab => (
            <NavigationTab
              key={tab.label}
              label={tab.label}
              href={tab.route}
              Icon={getTabIcon(
                routeIcons[tab.label as keyof typeof routeIcons]
              )}
              isLeftRailOpen={isLeftRailOpen}
            />
          ))}
      </NavigationMenu.List>
      <Divider />
      <NavigationMenu.List className={classes.list}>
        {secondaryRoutes.map(tab => {
          const isExternalLink = tab.route.startsWith('http');

          return (
            <NavigationTab
              key={tab.label}
              label={tab.label}
              href={tab.route}
              Icon={getTabIcon(tab.iconType)}
              isLeftRailOpen={isLeftRailOpen}
              disableLink={tab.disableLink}
              target={isExternalLink ? '_blank' : '_self'}
            />
          );
        })}
      </NavigationMenu.List>
    </NavigationMenu.Root>
  );
};
export default NavigationTabs;
